/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Telephony settings hub.
 *
 * Mirrors PaymentSettingsPage: a chooser that shows connection state at a
 * glance and routes to the pages that do the work. The three cards are ordered
 * by dependency -- there is no point mapping a number before the account is
 * connected, and no point routing a number that is not mapped -- so a first-time
 * admin can work straight down the page.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  WarningAmber,
  PhoneInTalk,
  Dialpad,
  AltRoute,
  GraphicEq,
  Lock,
  AccountTree,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";
import { v2 } from "styles/tokens/v2";
import TelephonyHubV2 from "./telephony/TelephonyHubV2";

const THEME_BLUE = colors.callCenter.themeBlue;
const THEME_BLUE_HOVER = colors.callCenter.themeBlueHover;
const CARD_BORDER = `1px solid ${colors.callCenter.cardBorder}`;

function StatusChip({ enabled, configured }) {
  if (enabled && configured) {
    return <Chip icon={<CheckCircle />} label="Connected" color="success" size="small" />;
  }
  if (configured) {
    return (
      <Chip icon={<WarningAmber />} label="Configured · Disabled" color="warning" size="small" />
    );
  }
  return <Chip label="Not connected" size="small" variant="outlined" />;
}

function CountChip({ count, noun }) {
  if (!count) return <Chip label={`No ${noun}`} size="small" variant="outlined" />;
  return <Chip label={`${count} ${noun}`} size="small" color="info" />;
}

function RecordingChip({ settings }) {
  if (!settings || !settings.recording_enabled) {
    return <Chip label="Recording off" size="small" variant="outlined" />;
  }
  if (settings.transcription_enabled) {
    return <Chip icon={<CheckCircle />} label="Recording · Transcribing" color="success" size="small" />;
  }
  return <Chip icon={<CheckCircle />} label="Recording" color="success" size="small" />;
}

function SettingCard({ icon, title, description, status, action, onClick, disabled, disabledHint }) {
  const isV2Card = useTheme()?.pearlUi === "v2";
  return (
    <Card
      sx={{
        height: "100%",
        p: 3,
        display: "flex",
        flexDirection: "column",
        border: CARD_BORDER,
        boxShadow: "none",
        borderRadius: "12px",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
        <MDBox
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            bgcolor: "#d7e3fc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: THEME_BLUE,
            // v2 icons inherit their size; the tile asks for 22px.
            ...(isV2Card && { fontSize: 22 }),
          }}
        >
          {icon}
        </MDBox>
        {status}
      </MDBox>
      <MDTypography variant="h5" fontWeight="medium" mb={1}>
        {title}
      </MDTypography>
      <MDTypography variant="body2" color="text" mb={3} sx={{ flexGrow: 1 }}>
        {disabled && disabledHint ? disabledHint : description}
      </MDTypography>
      <Button
        fullWidth
        disableElevation
        disabled={disabled}
        onClick={onClick}
        sx={{
          backgroundColor: THEME_BLUE,
          color: "#fff !important",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.95rem",
          borderRadius: "6px",
          py: 1.25,
          boxShadow: "none",
          "&:hover": { backgroundColor: THEME_BLUE_HOVER, boxShadow: "none" },
          "&.Mui-disabled": isV2Card
            ? { backgroundColor: v2.hover, color: `${v2.text3} !important` }
            : { backgroundColor: "#C9D4E5", color: "#fff !important" },
        }}
      >
        {action}
      </Button>
    </Card>
  );
}

export default function TelephonySettingsPage() {
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";
  const location = useLocation();
  const { axiosInstance } = useAuth();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();

  // The route key is deliberately exempt from entitlement filtering (an admin
  // has to reach this page to buy the module), so the lock lives here instead.
  const locked = !entitlementsLoading && !isModuleEnabled("CALL_CENTER");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [menus, setMenus] = useState([]);
  const [toast, setToast] = useState("");

  useEffect(() => {
    const msg = location.state?.telephonyMessage;
    if (msg) {
      setToast(msg);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Numbers and queues are secondary: a failure there should not stop the
      // page rendering the connection state, which is what the admin came for.
      const settingsRes = await axiosInstance.get("/telephony/settings");
      setSettings(settingsRes.data);

      const [numbersRes, queuesRes, menusRes] = await Promise.allSettled([
        axiosInstance.get("/telephony/numbers"),
        axiosInstance.get("/call-center/queues", { params: { with_stats: false } }),
        axiosInstance.get("/call-center/ivr-menus", { params: { include_inactive: true } }),
      ]);
      setNumbers(
        numbersRes.status === "fulfilled" && Array.isArray(numbersRes.value.data)
          ? numbersRes.value.data
          : []
      );
      setQueues(
        queuesRes.status === "fulfilled" && Array.isArray(queuesRes.value.data)
          ? queuesRes.value.data
          : []
      );
      setMenus(
        menusRes.status === "fulfilled" && Array.isArray(menusRes.value.data)
          ? menusRes.value.data
          : []
      );
    } catch (err) {
      setError(
        getApiErrorMessage(err, "Unable to load telephony settings.")
      );
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    // Wait for the entitlement snapshot, and do not fetch at all when the
    // module is absent -- the three requests would only return 403s.
    if (entitlementsLoading || locked) {
      setLoading(false);
      return;
    }
    load();
  }, [load, entitlementsLoading, locked]);

  const configured = Boolean(settings?.is_configured);
  const enabled = Boolean(settings?.enabled);
  const mappedNumbers = numbers.filter((n) => n.inbound_queue_id || n.direct_user_id).length;

  const connectLabel = configured ? (enabled ? "Manage connection" : "Enable Twilio") : "Connect Twilio";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 3 : 4} pb={6} px={isV2 ? 0 : 2}>
        {isV2 ? (
          <PageHeader title="Telephony" summary="Connect Twilio, map your phone numbers and decide where inbound calls go" />
        ) : (
        <MDBox display="flex" alignItems="center" gap={1} mb={3}>
          <IconButton onClick={() => navigate("/settings")}>
            <ArrowBack />
          </IconButton>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              Telephony
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Connect Twilio, map your phone numbers and decide where inbound calls go
            </MDTypography>
          </MDBox>
        </MDBox>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {/* getApiErrorMessage handles the object shapes the API actually
                returns (FastAPI detail arrays, the RBAC 403 payload). The old
                JSON.stringify fallback rendered a raw object dump at the user
                the one time it fired. */}
            {typeof error === "string" ? error : getApiErrorMessage(error, "Something went wrong.")}
          </Alert>
        )}

        {/* Surfaced here rather than only on the credential page: without a key
            the connection form cannot save at all, and an admin should find that
            out before typing a secret into it. */}
        {settings && settings.encryption_available === false && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Credential encryption is not configured on this server, so Twilio
            credentials cannot be saved. Ask your administrator to set
            SECRET_ENCRYPTION_KEYS and restart the API.
          </Alert>
        )}

        {locked ? (
          <Card sx={{ border: isV2 ? `1px solid ${v2.border}` : CARD_BORDER, p: 5, textAlign: "center" }}>
            <Lock sx={{ fontSize: 44, color: THEME_BLUE, mb: 1.5 }} />
            <MDTypography variant="h5" fontWeight="medium" color="dark" mb={1}>
              Call Center is not on your plan
            </MDTypography>
            <MDTypography variant="body2" color="text" sx={{ maxWidth: 520, mx: "auto" }}>
              Telephony settings unlock once the Call Center add-on is added to
              your subscription. Talk to your account manager to enable inbound
              and outbound calling, queues, routing and call recording.
            </MDTypography>
          </Card>
        ) : loading ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </MDBox>
        ) : isV2 ? (
          <TelephonyHubV2
            settings={settings}
            numbers={numbers}
            queues={queues}
            menus={menus}
            configured={configured}
            enabled={enabled}
            onOpen={(path) => navigate(path)}
          />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <SettingCard
                icon={<PhoneInTalk />}
                title="Twilio connection"
                description="Enter your Twilio Account SID and API key so Pearl can place and receive calls on your numbers."
                status={<StatusChip enabled={enabled} configured={configured} />}
                action={connectLabel}
                onClick={() => navigate("/settings/telephony/twilio")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <SettingCard
                icon={<Dialpad />}
                title="Phone numbers"
                description="Import the numbers on your Twilio account and choose which queue each one rings."
                status={<CountChip count={numbers.length} noun="numbers" />}
                action={numbers.length ? `Manage numbers (${mappedNumbers} mapped)` : "Import numbers"}
                onClick={() => navigate("/settings/telephony/numbers")}
                disabled={!configured}
                disabledHint="Connect your Twilio account first — Pearl needs it to read the numbers you own."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <SettingCard
                icon={<AccountTree />}
                title="Phone menus"
                description="Give callers a choice before they reach anyone: press 1 for sales, 2 for service."
                status={<CountChip count={menus.filter((m) => m.is_active).length} noun="live" />}
                action={menus.length ? "Manage phone menus" : "Build your first menu"}
                onClick={() => navigate("/settings/telephony/ivr")}
                disabled={!queues.length}
                disabledHint="Create a queue first — a menu key has to send the caller somewhere."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <SettingCard
                icon={<AltRoute />}
                title="Queues & routing"
                description="Build queues from your teams and write the rules that decide which queue an inbound call lands in."
                status={<CountChip count={queues.length} noun="queues" />}
                action={queues.length ? "Manage queues & routing" : "Create your first queue"}
                onClick={() => navigate("/settings/telephony/routing")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <SettingCard
                icon={<GraphicEq />}
                title="Recording & transcription"
                description="Decide what is recorded, what callers are told, how long audio is kept, and whether calls are transcribed."
                status={<RecordingChip settings={settings} />}
                action="Manage recording"
                onClick={() => navigate("/settings/telephony/recording")}
                disabled={!configured}
                disabledHint="Connect your Twilio account first — there is nothing to record until calls can reach Pearl."
              />
            </Grid>
          </Grid>
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setToast("")} sx={{ width: "100%" }}>
          {toast}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
